'use client';

import { Root as Switch } from '@radix-ui/react-switch';
import { cva, type VariantProps } from 'class-variance-authority';
import { type ComponentPropsWithRef, type CSSProperties } from 'react';
import { cn } from '@/lib/utils';
import { SwatchIndicator } from '@/shared-ui/atoms/swatch-indicator';
import { Typography } from '@/shared-ui/atoms/typography/typography';
import { Tooltip } from '@/shared-ui/molecules/tooltip';

const swatchVariants = cva(
    'rounded-full flex-shrink-0 relative group enabled:cursor-pointer data-[state=unchecked]:enabled:hover:outline data-[state=unchecked]:enabled:hover:outline-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
    {
        variants: {
            size: {
                default:
                    'size-8 data-[state=checked]:outline-offset-4 data-[state=unchecked]:enabled:hover:outline-offset-4',
                sm: 'size-[18px] data-[state=checked]:outline-offset-[2.4px] data-[state=unchecked]:enabled:hover:outline-offset-[2.4px]',
            },
            status: {
                default: '',
                unavailable: '',
            },
        },
        defaultVariants: {
            size: 'default',
            status: 'default',
        },
    }
);

interface BaseSwatchProps
    extends Omit<ComponentPropsWithRef<typeof Switch>, 'children'>,
        VariantProps<typeof swatchVariants> {
    colour?: string;
    label?: string;
    labelClassName?: string;
    contentClassName?: string;
    contentStyle?: CSSProperties;
}

type SwatchTooltipProps =
    | {
          hasTooltip: true;
          tooltipText: string;
      }
    | {
          hasTooltip?: false;
          tooltipText?: never;
      };

export type SwatchProps = BaseSwatchProps & SwatchTooltipProps;

export const Swatch = ({
    ref,
    className,
    size,
    status,
    colour,
    label,
    labelClassName,
    contentClassName,
    contentStyle,
    hasTooltip = false,
    tooltipText,
    ...props
}: SwatchProps) => {
    const swatchNode = (
        <Switch
            ref={ref}
            className={cn(
                swatchVariants({ size, status }),
                'items-center justify-center data-[state=checked]:outline-1 data-[state=checked]:outline-foreground',
                className
            )}
            {...props}>
            <SwatchIndicator
                aria-hidden
                colour={colour}
                status={status ?? 'default'}
                data-slot="swatch-content"
                className={cn('pointer-events-none absolute inset-0', contentClassName)}
                style={contentStyle}
            />

            {typeof label === 'string' && label.trim().length > 0 ? (
                <span className={cn('sr-only', labelClassName)}>{label}</span>
            ) : null}
        </Switch>
    );

    if (!hasTooltip) {
        return swatchNode;
    }

    return (
        <Tooltip.Root>
            <Tooltip.Trigger asChild>
                <span className="inline-flex">{swatchNode}</span>
            </Tooltip.Trigger>
            <Tooltip.Content className="!w-fit">
                <Tooltip.Title>
                    <Typography as="span" variant="p" className="normal-case font-light">
                        {tooltipText}
                    </Typography>
                </Tooltip.Title>
            </Tooltip.Content>
        </Tooltip.Root>
    );
};
